'use client'

import { useState, useCallback, useMemo, useEffect } from 'react'
import type { 
  OnboardingStep, 
  OnboardingState, 
  OnboardingStepId,
  OnboardingContextValue 
} from '@/types/onboarding'
import posthog from 'posthog-js'
import { useAuth } from '@/lib/auth-provider'

const ONBOARDING_STEPS: OnboardingStep[] = [
  // Welcome & Overview
  {
    id: 'welcome',
    title: 'Bienvenue dans VisualGrid Synoptique',
    titleKey: 'onboarding.welcome.title',
    description: 'Découvrez comment créer vos synoptiques LED en quelques étapes simples.',
    descriptionKey: 'onboarding.welcome.description',
    showSpotlight: false,
    showSkip: true,
    showBack: false,
  },
  {
    id: 'overview-steps',
    title: 'Les 4 étapes du workflow',
    titleKey: 'onboarding.overviewSteps.title',
    description: 'Le Synoptique est divisé en 4 étapes.',
    descriptionKey: 'onboarding.overviewSteps.description',
    target: '[data-onboarding="workflow-tabs"]',
    targetPosition: 'bottom',
    tooltipPosition: 'bottom',
    showSpotlight: true,
    spotlightPadding: 8,
    showSkip: true,
    showBack: true,
  },

  // Step 1: Test Pattern / Mire
  {
    id: 'step1-intro',
    title: 'Étape 1 : Configuration de la Mire',
    titleKey: 'onboarding.step1Intro.title',
    description: 'La première étape consiste à créer votre mire. Définissez les paramètres des panneaux.',
    descriptionKey: 'onboarding.step1Intro.description',
    target: '[data-onboarding="grid-dimensions"]',
    targetPosition: 'left',
    tooltipPosition: 'left',
    workflowStep: 'testPattern',
    showSpotlight: true,
    spotlightPadding: 12,
    showSkip: true,
    showBack: true,
  },
  {
    id: 'step1-create-grid',
    title: 'Personnalisez votre mire',
    titleKey: 'onboarding.step1Customize.title',
    description: 'Modifiez au moins 2 paramètres (couleurs, patterns, etc.)',
    descriptionKey: 'onboarding.step1Customize.description',
    target: '[data-onboarding="grid-customization"]',
    targetPosition: 'left',
    tooltipPosition: 'right',
    tooltipFixedRight: true,
    workflowStep: 'testPattern',
    showSpotlight: true,
    spotlightPadding: 12,
    disableOverlay: true,
    allowInteraction: true,
    nextOnAction: true,
    requiredAction: {
      type: 'mire-customized',
      count: 2
    },
    showSkip: true,
    showBack: true,
  },
  {
    id: 'step1-add-to-list',
    title: 'Ajoutez votre mire',
    titleKey: 'onboarding.step1AddToList.title',
    description: 'Donnez un nom à votre mire et cliquez sur "Ajouter à la liste". Vous pouvez créer plusieurs mires différentes.',
    descriptionKey: 'onboarding.step1AddToList.description',
    target: '[data-onboarding="add-to-list-section"]',
    targetPosition: 'top',
    tooltipPosition: 'right',
    workflowStep: 'testPattern',
    showSpotlight: true,
    spotlightPadding: 8,
    allowInteraction: true,
    nextOnAction: true,
    requiredAction: {
      type: 'mire-added-to-list',
    },
    showSkip: true,
    showBack: true,
  },
  {
    id: 'step1-use-pattern',
    title: 'Validez votre mire',
    titleKey: 'onboarding.step1UsePattern.title',
    description: 'Cliquez sur "Utiliser la mire" pour passer à l\'étape suivante.',
    descriptionKey: 'onboarding.step1UsePattern.description',
    target: '[data-onboarding="use-pattern-btn"]',
    targetPosition: 'top',
    tooltipPosition: 'top',
    workflowStep: 'testPattern',
    showSpotlight: true,
    spotlightPadding: 8,
    allowInteraction: true,
    nextOnAction: true,
    requiredAction: {
      type: 'mire-used',
    },
    showSkip: true,
    showBack: true,
  },

  // Step 2: Data / Câblage signal
  {
    id: 'step2-intro',
    title: 'Étape 2 : Processeur LED et cablâge du signal',
    titleKey: 'onboarding.step2Intro.title',
    description: 'C\'est ici que vous allez configurer votre processeur et câbler le signal de vos panneaux LED.',
    descriptionKey: 'onboarding.step2Intro.description',
    target: '[data-onboarding="step-data"]',
    targetPosition: 'bottom',
    tooltipPosition: 'bottom',
    workflowStep: 'data',
    showSpotlight: true,
    spotlightPadding: 4,
    showSkip: true,
    showBack: true,
    delay: 500,
  },
  {
    id: 'step2-select-and-drag-processor',
    title: 'Placez un processeur',
    titleKey: 'onboarding.step2SelectAndDragProcessor.title',
    description: 'Recherchez "MCTRL 4K" dans la liste des processeurs à gauche, puis glissez-déposez-le sur le workspace.',
    descriptionKey: 'onboarding.step2SelectAndDragProcessor.description',
    target: '[data-onboarding="processor-list"]',
    targetPosition: 'right',
    tooltipPosition: 'right',
    workflowStep: 'data',
    showSpotlight: true,
    spotlightPadding: 8,
    allowInteraction: true,
    nextOnAction: true,
    requiredAction: {
      type: 'processor-placed',
    },
    showSkip: true,
    showBack: true,
  },
  {
    id: 'step2-click-processor',
    title: 'Sélectionnez le processeur',
    titleKey: 'onboarding.step2ClickProcessor.title',
    description: 'Cliquez sur le processeur dans le workspace pour le sélectionner et voir ses ports de connexion.',
    descriptionKey: 'onboarding.step2ClickProcessor.description',
    target: '[data-onboarding="workspace-processor"]',
    targetPosition: 'center',
    tooltipPosition: 'right',
    tooltipFixedRight: true,
    workflowStep: 'data',
    showSpotlight: true,
    spotlightPadding: 16,
    allowInteraction: true,
    nextOnAction: true,
    requiredAction: {
      type: 'processor-selected',
    },
    showSkip: false,
    showBack: true,
  },
  {
    id: 'step2-select-port',
    title: 'Sélectionnez le Port 1',
    titleKey: 'onboarding.step2SelectPort.title',
    description: 'Dans le panneau du processeur en bas, cliquez sur le Port 1 pour commencer le câblage.',
    descriptionKey: 'onboarding.step2SelectPort.description',
    target: '[data-onboarding="port-1"]',
    targetPosition: 'top',
    tooltipPosition: 'top',
    workflowStep: 'data',
    showSpotlight: true,
    spotlightPadding: 4,
    allowInteraction: true,
    nextOnAction: true,
    requiredAction: {
      type: 'port-selected',
      value: 1
    },
    showSkip: false,
    showBack: true,
  },
  {
    id: 'step2-click-mire',
    title: 'Commencez le câblage',
    titleKey: 'onboarding.step2ClickMire.title',
    description: 'Cliquez sur une dalle de la mire pour connecter le premier panneau.',
    descriptionKey: 'onboarding.step2ClickMire.description',
    target: '[data-onboarding="workspace"]',
    targetPosition: 'center',
    tooltipPosition: 'right',
    tooltipFixedRight: true,
    workflowStep: 'data',
    showSpotlight: false,
    disableOverlay: true,
    allowInteraction: true,
    nextOnAction: true,
    requiredAction: {
      type: 'panels-cabled',
      count: 1
    },
    showSkip: false,
    showBack: true,
  },
  {
    id: 'step2-cable-panels',
    title: 'Câblez votre mur de LED',
    titleKey: 'onboarding.step2CablePanels.title',
    description: 'Déplacez-vous avec les flèches (↑, ↓, ←, →) sur la mire pour câbler votre mur de LED. Suivez un chemin logique.',
    descriptionKey: 'onboarding.step2CablePanels.description',
    target: '[data-onboarding="workspace"]',
    targetPosition: 'center',
    tooltipPosition: 'right',
    tooltipFixedRight: true,
    workflowStep: 'data',
    showSpotlight: false,
    disableOverlay: true,
    allowInteraction: true,
    nextOnAction: true,
    requiredAction: {
      type: 'panels-cabled',
      count: 6
    },
    showSkip: false,
    showBack: true,
  },
  {
    id: 'step2-create-redundancy',
    title: 'Créez une redondance',
    titleKey: 'onboarding.step2CreateRedundancy.title',
    description: 'Glissez le Port 2 sur le Port 1 pour créer une redondance.',
    descriptionKey: 'onboarding.step2CreateRedundancy.description',
    target: '[data-onboarding="port-2"]',
    targetPosition: 'top',
    tooltipPosition: 'top',
    workflowStep: 'data',
    showSpotlight: true,
    spotlightPadding: 4,
    dynamicTarget: true,
    allowInteraction: true,
    nextOnAction: true,
    requiredAction: {
      type: 'redundancy-created',
    },
    showSkip: false,
    showBack: true,
  },

  // Step 3: Power / Alimentation
  {
    id: 'step3-intro',
    title: 'Étape 3 : Alimentation (Power)',
    titleKey: 'onboarding.step3Intro.title',
    description: 'Passons maintenant à la configuration de l\'alimentation électrique de votre mur LED.',
    descriptionKey: 'onboarding.step3Intro.description',
    target: '[data-onboarding="step-cabling"]',
    targetPosition: 'bottom',
    tooltipPosition: 'bottom',
    workflowStep: 'cabling',
    showSpotlight: true,
    spotlightPadding: 4,
    showSkip: true,
    showBack: true,
    delay: 500,
  },
  {
    id: 'step3-select-and-drag-cabinet',
    title: 'Placez une armoire',
    titleKey: 'onboarding.step3SelectAndDragCabinet.title',
    description: 'Recherchez "Legrand" dans la liste des armoires à gauche, puis glissez-déposez-la sur le workspace.',
    descriptionKey: 'onboarding.step3SelectAndDragCabinet.description',
    target: '[data-onboarding="cabinet-list"]',
    targetPosition: 'right',
    tooltipPosition: 'right',
    workflowStep: 'cabling',
    showSpotlight: true,
    spotlightPadding: 8,
    allowInteraction: true,
    nextOnAction: true,
    requiredAction: {
      type: 'cabinet-placed',
    },
    showSkip: true,
    showBack: true,
  },
  {
    id: 'step3-click-cabinet',
    title: 'Sélectionnez l\'armoire',
    titleKey: 'onboarding.step3ClickCabinet.title',
    description: 'Cliquez sur l\'armoire dans le workspace pour la sélectionner.',
    descriptionKey: 'onboarding.step3ClickCabinet.description',
    target: '[data-onboarding="workspace-cabinet"]',
    targetPosition: 'center',
    tooltipPosition: 'right',
    tooltipFixedRight: true,
    workflowStep: 'cabling',
    showSpotlight: true,
    spotlightPadding: 16,
    allowInteraction: true,
    nextOnAction: true,
    requiredAction: {
      type: 'cabinet-selected',
    },
    showSkip: false,
    showBack: true,
  },
  {
    id: 'step3-select-outlet',
    title: 'Sélectionnez la prise PC16 n°1',
    titleKey: 'onboarding.step3SelectOutlet.title',
    description: 'Dans le panneau de l\'armoire en bas, cliquez sur la première prise PC16 pour commencer le câblage électrique.',
    descriptionKey: 'onboarding.step3SelectOutlet.description',
    target: '[data-onboarding="outlet-pc16-1"]',
    targetPosition: 'top',
    tooltipPosition: 'right',
    tooltipFixedRight: true,
    workflowStep: 'cabling',
    showSpotlight: true,
    spotlightPadding: 8,
    allowInteraction: true,
    nextOnAction: true,
    requiredAction: {
      type: 'outlet-selected',
    },
    showSkip: true,
    showBack: true,
  },
  {
    id: 'step3-cable-mire',
    title: 'Câblez l\'alimentation',
    titleKey: 'onboarding.step3CableMire.title',
    description: 'Cliquez sur les cellules de la mire pour connecter l\'alimentation aux panneaux.',
    descriptionKey: 'onboarding.step3CableMire.description',
    target: '[data-onboarding="workspace"]',
    targetPosition: 'center',
    tooltipPosition: 'right',
    tooltipFixedRight: true,
    workflowStep: 'cabling',
    showSpotlight: false,
    disableOverlay: true,
    allowInteraction: true,
    nextOnAction: true,
    requiredAction: {
      type: 'panels-cabled',
      count: 6
    },
    showSkip: false,
    showBack: true,
  },

  // Step 4: Export
  {
    id: 'step4-intro',
    title: 'Étape 4 : Export',
    titleKey: 'onboarding.step4Intro.title',
    description: 'Dernière étape : documentez et exportez votre schéma synoptique.',
    descriptionKey: 'onboarding.step4Intro.description',
    target: '[data-onboarding="step-export"]',
    targetPosition: 'bottom',
    tooltipPosition: 'bottom',
    workflowStep: 'export',
    showSpotlight: true,
    spotlightPadding: 4,
    showSkip: true,
    showBack: true,
    delay: 500,
  },
  {
    id: 'step4-cartouche',
    title: 'Remplissez la cartouche',
    titleKey: 'onboarding.step4Cartouche.title',
    description: 'La cartouche d\'informations apparaît à gauche. Renseignez au moins 2 champs (client, événement, lieu, etc.).',
    descriptionKey: 'onboarding.step4Cartouche.description',
    target: '[data-onboarding="cartouche"]',
    targetPosition: 'right',
    tooltipPosition: 'right',
    workflowStep: 'export',
    showSpotlight: true,
    spotlightPadding: 12,
    allowInteraction: true,
    nextOnAction: true,
    requiredAction: {
      type: 'cartouche-filled',
      count: 3,
    },
    showSkip: true,
    showBack: true,
  },
  {
    id: 'step4-export-options',
    title: 'Exportez en PNG',
    titleKey: 'onboarding.step4ExportOptions.title',
    description: 'Cliquez sur le bouton d\'export PNG pour télécharger votre synoptique. En démo, un watermark sera ajouté.',
    descriptionKey: 'onboarding.step4ExportOptions.description',
    target: '[data-onboarding="export-options"]',
    targetPosition: 'right',
    tooltipPosition: 'right',
    workflowStep: 'export',
    showSpotlight: true,
    spotlightPadding: 12,
    allowInteraction: true,
    nextOnAction: true,
    requiredAction: {
      type: 'png-exported',
    },
    showSkip: true,
    showBack: true,
  },

  // Final: Save & Cloud
  {
    id: 'final-save-menu',
    title: 'Sauvegarde et gestion',
    titleKey: 'onboarding.finalSaveMenu.title',
    description: 'Le menu Import/Export vous permet de sauvegarder votre projet localement, d\'importer un projet ou de l\'exporter en fichier .vgsyn.',
    descriptionKey: 'onboarding.finalSaveMenu.description',
    target: '[data-onboarding="save-menu"]',
    targetPosition: 'bottom',
    tooltipPosition: 'bottom',
    showSpotlight: true,
    spotlightPadding: 12,
    allowInteraction: true,
    showSkip: true,
    showBack: true,
  },
  {
    id: 'final-cloud',
    title: 'VisualCloud',
    titleKey: 'onboarding.finalCloud.title',
    description: 'Avec le VisualCloud, synchronisez vos projets en ligne et accédez-y depuis n\'importe quel appareil.',
    descriptionKey: 'onboarding.finalCloud.description',
    target: '[data-onboarding="cloud-menu"]',
    targetPosition: 'bottom',
    tooltipPosition: 'bottom',
    showSpotlight: true,
    spotlightPadding: 8,
    allowInteraction: true,
    showSkip: true,
    showBack: true,
  },
  {
    id: 'completed',
    title: 'Félicitations !',
    titleKey: 'onboarding.completed.title',
    description: 'Vous maîtrisez maintenant les bases de VisualGrid Synoptique. Explorez les fonctionnalités avancées !',
    descriptionKey: 'onboarding.completed.description',
    showSpotlight: false,
    showSkip: false,
    showBack: true,
  },
]

const STORAGE_KEY = 'visualgrid_onboarding_completed'

export function useOnboarding() {
  const { isPro } = useAuth()
  const [state, setState] = useState<OnboardingState>({
    isActive: false,
    currentStepIndex: 0,
    currentStepId: 'welcome',
    completedSteps: [],
    isTransitioning: false,
    isPaused: false,
    cameFromBack: false,
  })

  // Track dragging state for highlighting workspace during drag
  const [isDragging, setIsDraggingState] = useState(false)
  const [draggingType, setDraggingType] = useState<'processor' | 'cabinet' | null>(null)

  // Check if onboarding was already completed
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const completed = localStorage.getItem(STORAGE_KEY)
      if (completed === 'true') {
        setState(prev => ({ ...prev, completedSteps: ['completed'] }))
      }
    }
  }, [])

  const currentStep = useMemo(() => {
    return ONBOARDING_STEPS[state.currentStepIndex] || null
  }, [state.currentStepIndex])

  const startOnboarding = useCallback(() => {
    posthog.capture('onboarding:flow_start', {
      entry_point: 'manual_start',
      is_pro_user: isPro,
    })
    
    setState({
      isActive: true,
      currentStepIndex: 0,
      currentStepId: 'welcome',
      completedSteps: [],
      isTransitioning: false,
      isPaused: false,
      cameFromBack: false,
    })
  }, [isPro])

  const stopOnboarding = useCallback(() => {
    setState(prev => ({
      ...prev,
      isActive: false,
      isTransitioning: false,
      isPaused: false,
    }))
  }, [])

  const nextStep = useCallback(() => {
    setState(prev => {
      const nextIndex = prev.currentStepIndex + 1
      const currentStepData = ONBOARDING_STEPS[prev.currentStepIndex]
      
      // Track step completion
      posthog.capture('onboarding:step_complete', {
        step_id: prev.currentStepId,
        step_number: prev.currentStepIndex + 1,
        workflow_step: currentStepData?.workflowStep || null,
        duration_seconds: 0, // TODO: Calculate actual duration
      })
      
      if (nextIndex >= ONBOARDING_STEPS.length) {
        // Mark as completed
        if (typeof window !== 'undefined') {
          localStorage.setItem(STORAGE_KEY, 'true')
        }
        
        // Track flow completion
        posthog.capture('onboarding:flow_complete', {
          total_duration_seconds: 0, // TODO: Calculate actual duration
          steps_skipped: 0, // TODO: Track skipped steps
          is_first_completion: true,
        })
        
        return {
          ...prev,
          isActive: false,
          completedSteps: [...prev.completedSteps, prev.currentStepId, 'completed'],
        }
      }

      const nextStepData = ONBOARDING_STEPS[nextIndex]
      return {
        ...prev,
        currentStepIndex: nextIndex,
        currentStepId: nextStepData.id,
        completedSteps: [...prev.completedSteps, prev.currentStepId],
        isTransitioning: true,
        cameFromBack: false,
      }
    })

    // Reset transitioning after animation
    setTimeout(() => {
      setState(prev => ({ ...prev, isTransitioning: false }))
    }, 300)
  }, [])

  const prevStep = useCallback(() => {
    setState(prev => {
      if (prev.currentStepIndex <= 0) return prev

      const prevIndex = prev.currentStepIndex - 1
      const prevStepData = ONBOARDING_STEPS[prevIndex]

      return {
        ...prev,
        currentStepIndex: prevIndex,
        currentStepId: prevStepData.id,
        isTransitioning: true,
        cameFromBack: true,
      }
    })

    setTimeout(() => {
      setState(prev => ({ ...prev, isTransitioning: false }))
    }, 300)
  }, [])

  const goToStep = useCallback((stepId: OnboardingStepId) => {
    const stepIndex = ONBOARDING_STEPS.findIndex(s => s.id === stepId)
    if (stepIndex === -1) return

    setState(prev => ({
      ...prev,
      currentStepIndex: stepIndex,
      currentStepId: stepId,
      isTransitioning: true,
    }))

    setTimeout(() => {
      setState(prev => ({ ...prev, isTransitioning: false }))
    }, 300)
  }, [])

  const skipOnboarding = useCallback(() => {
    // Track onboarding skip
    posthog.capture('onboarding:flow_skip', {
      current_step_id: state.currentStepId,
      step_number: state.currentStepIndex + 1,
      total_duration_seconds: 0, // TODO: Calculate actual duration
    })
    
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, 'true')
    }
    setState(prev => ({
      ...prev,
      isActive: false,
      completedSteps: [...prev.completedSteps, 'completed'],
    }))
  }, [state.currentStepId, state.currentStepIndex])

  const pauseOnboarding = useCallback(() => {
    setState(prev => ({ ...prev, isPaused: true }))
  }, [])

  const resumeOnboarding = useCallback(() => {
    setState(prev => ({ ...prev, isPaused: false }))
  }, [])

  const completeAction = useCallback((actionType: string, currentCount?: number) => {
    // Check if current step requires this action
    if (!currentStep?.requiredAction) return
    if (currentStep.requiredAction.type !== actionType) return

    // If user came from back navigation, don't auto-advance
    // (the Continue button will be shown instead)
    if (state.cameFromBack) return

    // Check count requirement
    if (currentStep.requiredAction.count !== undefined) {
      if (currentCount === undefined || currentCount < currentStep.requiredAction.count) return
    }

    // If nextOnAction is set, auto-advance
    if (currentStep.nextOnAction) {
      nextStep()
    }
  }, [currentStep, nextStep, state.cameFromBack])

  const isStepCompleted = useCallback((stepId: OnboardingStepId) => {
    return state.completedSteps.includes(stepId)
  }, [state.completedSteps])

  const resetOnboarding = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY)
    }
    setState({
      isActive: false,
      currentStepIndex: 0,
      currentStepId: 'welcome',
      completedSteps: [],
      isTransitioning: false,
      isPaused: false,
      cameFromBack: false,
    })
  }, [])

  const setDragging = useCallback((dragging: boolean, type?: 'processor' | 'cabinet' | null) => {
    setIsDraggingState(dragging)
    setDraggingType(type || null)
  }, [])

  const hasCompletedOnboarding = useMemo(() => {
    return state.completedSteps.includes('completed')
  }, [state.completedSteps])

  return {
    state,
    currentStep,
    steps: ONBOARDING_STEPS,
    startOnboarding,
    stopOnboarding,
    nextStep,
    prevStep,
    goToStep,
    skipOnboarding,
    pauseOnboarding,
    resumeOnboarding,
    completeAction,
    isStepCompleted,
    resetOnboarding,
    hasCompletedOnboarding,
    isDragging,
    draggingType,
    setDragging,
  }
}

export { ONBOARDING_STEPS }
